import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthShell } from "./Login";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell heading="Invalid link" sub="This reset link is missing a token.">
        <div className="mt-10 border border-red-500/30 bg-ink-paper/40 p-8 text-center" data-testid="reset-invalid">
          <XCircle size={40} className="text-red-400 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white/70 text-sm">
            The reset link appears broken. Please request a new one below.
          </p>
          <Link
            to="/forgot-password"
            data-testid="new-reset-link"
            className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300"
          >
            Request a new link
          </Link>
        </div>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("Passwords do not match.");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: form.new_password,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell heading="Set a new password" sub="Choose a strong password — at least 8 characters.">
      {done ? (
        <div className="mt-10 border border-gold-500/25 bg-ink-paper/40 p-8 text-center" data-testid="reset-done-panel">
          <CheckCircle2 size={40} className="text-gold-500 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-serif text-2xl text-white">Password reset</p>
          <p className="mt-3 text-sm text-white/60">Redirecting you to sign in…</p>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="reset-form">
            <Input
              required
              type="password"
              placeholder="New password"
              autoComplete="new-password"
              minLength={8}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="reset-new-password"
              className="bg-ink-paper border-gold-500/25 text-white placeholder:text-white/40 py-6"
            />
            <Input
              required
              type="password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              minLength={8}
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              data-testid="reset-confirm-password"
              className="bg-ink-paper border-gold-500/25 text-white placeholder:text-white/40 py-6"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold py-6"
            >
              {busy ? "Updating…" : "Reset password"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-white/60">
            Back to <Link to="/login" className="text-gold-400 hover:underline" data-testid="to-login">sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
